import React, { useState, useEffect } from 'react';
import { Table, Segmented, Checkbox, Button, Space, Typography, message, Popconfirm, Tag, Row, Col, Flex, Input } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type { CheckboxChangeEvent } from 'antd/es/checkbox';
import { CheckCircleOutlined, ClockCircleOutlined, LeftOutlined, CloseCircleOutlined, MinusCircleOutlined, RightOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import weekday from 'dayjs/plugin/weekday';
import localeData from 'dayjs/plugin/localeData';
import { appointmentApi } from '../api/appointment';
import { Appointment, AppointmentStatus } from '../api/model';

dayjs.extend(weekday);
dayjs.extend(localeData);

const statusOptions = [
  { label: '待确认', value: AppointmentStatus.Pending },
  { label: '已确认', value: AppointmentStatus.Confirmed },
  { label: '拒绝', value: AppointmentStatus.Rejected },
  { label: '取消', value: AppointmentStatus.Cancelled },
];

const Appointments: React.FC = () => {
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [loading, setLoading] = useState(false);
  const [currentWeekStart, setCurrentWeekStart] = useState(dayjs().startOf('week'));
  const [appointmentType, setAppointmentType] = useState<'future' | 'history'>('future');
  const [selectedStatuses, setSelectedStatuses] = useState<AppointmentStatus[]>([AppointmentStatus.Pending, AppointmentStatus.Confirmed]);
  // 添加选中的行状态
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  // 全选状态
  const [checkAll, setCheckAll] = useState(false);
  const [indeterminate, setIndeterminate] = useState(false);
  
  // 获取当前周的开始和结束时间
  const getCurrentWeekRange = () => {
    const start = currentWeekStart;
    const end = currentWeekStart.endOf('week');
    return { start, end };
  };

  // 格式化时间范围显示
  const formatWeekRange = () => {
    const { start, end } = getCurrentWeekRange();
    const isCurrentWeek = start.isSame(dayjs().startOf('week'), 'week');
    
    if (isCurrentWeek) {
      return `${start.format('YYYY-MM-DD')} ~ ${end.format('MM-DD')} (本周)`;
    }
    return `${start.format('YYYY-MM-DD')} ~ ${end.format('MM-DD')}`;
  };

  // 获取预约数据
  const fetchAppointments = async () => {
    setLoading(true);
    try {
      const { start, end } = getCurrentWeekRange();
      const params = {
        start_time: start.toISOString(),
        end_time: end.toISOString(),
        status_list: selectedStatuses.length > 0 ? selectedStatuses : undefined
      };
      
      const response = await appointmentApi.listAppointments(params);
      if (response.success) {
        // 根据类型筛选数据
        const filteredData = response.data.filter(appointment => {
          const appointmentEnd = dayjs(appointment.end_time);
          const now = dayjs();
          
          if (appointmentType === 'future') {
            return appointmentEnd.isAfter(now);
          } else {
            return appointmentEnd.isSameOrBefore(now);
          }
        });
        
        setAppointments(filteredData);
      } else {
        message.error('获取预约数据失败: ' + response.message);
      }
    } catch (error) {
      console.error('获取预约数据失败:', error);
      message.error('获取预约数据失败');
    } finally {
      setLoading(false);
    }
  };

  // 处理类型切换
  const handleTypeChange = (value: string | number) => {
    setAppointmentType(value as 'future' | 'history');
  };

  // 处理状态选择变化
  const handleStatusChange = (checkedValues: any[]) => {
    setSelectedStatuses(checkedValues);
    // 更新全选状态
    setCheckAll(checkedValues.length === statusOptions.length);
    setIndeterminate(checkedValues.length > 0 && checkedValues.length < statusOptions.length);
  };

  // 处理全选/取消全选
  const onCheckAllChange = (e: CheckboxChangeEvent) => {
    const checked = e.target.checked;
    setSelectedStatuses(checked ? statusOptions.map(option => option.value) : []);
    setCheckAll(checked);
    setIndeterminate(false);
  };

  // 上一周
  const goToPreviousWeek = () => {
    setCurrentWeekStart(currentWeekStart.subtract(1, 'week'));
  };

  // 下一周
  const goToNextWeek = () => {
    // 如果是历史预约且当前周是本周或之后，则不允许翻到下周
    if (appointmentType === 'history' && currentWeekStart.isSameOrAfter(dayjs().startOf('week'), 'week')) {
      return;
    }
    setCurrentWeekStart(currentWeekStart.add(1, 'week'));
  };

  // 确认预约
  const confirmAppointment = async (id: string) => {
    try {
      const response = await appointmentApi.confirmAppointment(id);
      if (response.success) {
        message.success('预约已确认');
        // 重新获取数据
        fetchAppointments();
      } else {
        message.error('确认预约失败: ' + response.message);
      }
    } catch (error) {
      console.error('确认预约失败:', error);
      message.error('确认预约失败');
    }
  };

  // 拒绝预约
  const rejectAppointment = async (id: string) => {
    try {
      const response = await appointmentApi.rejectAppointment(id);
      if (response.success) {
        message.success('预约已拒绝');
        // 重新获取数据
        fetchAppointments();
      } else {
        message.error('拒绝预约失败: ' + response.message);
      }
    } catch (error) {
      console.error('拒绝预约失败:', error);
      message.error('拒绝预约失败');
    }
  };

  // 批量确认预约
  const confirmAppointments = async () => {
    if (selectedRowKeys.length === 0) {
      message.warning('请至少选择一个预约');
      return;
    }
    
    try {
      // 通过发送多个请求来实现批量确认
      const promises = selectedRowKeys.map(id => appointmentApi.confirmAppointment(id as string));
      const responses = await Promise.all(promises);
      
      // 检查所有请求是否都成功
      const allSuccess = responses.every(response => response.success);
      
      if (allSuccess) {
        message.success(`成功确认 ${selectedRowKeys.length} 个预约`);
        // 清空选择
        setSelectedRowKeys([]);
        // 重新获取数据
        fetchAppointments();
      } else {
        // 如果有任何请求失败，显示错误信息
        const failedCount = responses.filter(response => !response.success).length;
        message.error(`批量确认预约失败: ${failedCount} 个预约确认失败`);
      }
    } catch (error) {
      console.error('批量确认预约失败:', error);
      message.error('批量确认预约失败');
    }
  };

  // 批量拒绝预约
  const rejectAppointments = async () => {
    if (selectedRowKeys.length === 0) {
      message.warning('请至少选择一个预约');
      return;
    }
    
    try {
      // 通过发送多个请求来实现批量拒绝
      const promises = selectedRowKeys.map(id => appointmentApi.rejectAppointment(id as string));
      const responses = await Promise.all(promises);
      
      // 检查所有请求是否都成功
      const allSuccess = responses.every(response => response.success);
      
      if (allSuccess) {
        message.success(`成功拒绝 ${selectedRowKeys.length} 个预约`);
        // 清空选择
        setSelectedRowKeys([]);
        // 重新获取数据
        fetchAppointments();
      } else {
        // 如果有任何请求失败，显示错误信息
        const failedCount = responses.filter(response => !response.success).length;
        message.error(`批量拒绝预约失败: ${failedCount} 个预约拒绝失败`);
      }
    } catch (error) {
      console.error('批量拒绝预约失败:', error);
      message.error('批量拒绝预约失败');
    }
  };

  // 状态标签映射
  const getStatusLabel = (status: AppointmentStatus) => {
    switch (status) {
      case AppointmentStatus.Pending: return '待确认';
      case AppointmentStatus.Confirmed: return '已确认';
      case AppointmentStatus.Cancelled: return '取消';
      case AppointmentStatus.Rejected: return '拒绝';
      default: return '';
    }
  };

  // 表格行选择配置
  const rowSelection = {
    selectedRowKeys,
    onChange: (selectedKeys: React.Key[]) => {
      setSelectedRowKeys(selectedKeys);
    },
  };

  // 更新全选状态
  useEffect(() => {
    setCheckAll(selectedStatuses.length === statusOptions.length);
    setIndeterminate(selectedStatuses.length > 0 && selectedStatuses.length < statusOptions.length);
  }, [selectedStatuses]);

  useEffect(() => {
    fetchAppointments();
  }, [currentWeekStart, appointmentType, selectedStatuses]);

  const columns: ColumnsType<Appointment> = [
    {
      title: '患者姓名',
      dataIndex: ['patient', 'user', 'name'],
      key: 'patient_name',
    },
    {
      title: '性别',
      dataIndex: ['patient', 'gender'],
      key: 'patient_gender',
      render: (gender: string) => (gender === 'male' ? '男' : gender === 'female' ? '女' : '未知'),
    },
    {
      title: '年龄',
      key: 'patient_age',
      render: (_, record) => {
        if (record.patient.birth_date) {
          const birthDate = dayjs(record.patient.birth_date);
          const now = dayjs();
          return now.diff(birthDate, 'year');
        }
        return '未知';
      },
    },
    {
      title: '预约时间',
      key: 'appointment_time',
      render: (_, record) => (
        <>
          <div>{dayjs(record.start_time).format('YYYY-MM-DD')}</div>
          <div>{dayjs(record.start_time).format('HH:mm')} - {dayjs(record.end_time).format('HH:mm')}</div>
        </>
      ),
    },
    {
      title: '地点',
      dataIndex: ['schedule_location', 'name'],
      key: 'schedule_location',
      render: (_, record) => record.schedule_location?.name || '-',
    },
    {
      title: '描述',
      dataIndex: 'description',
      key: 'description',
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      render: (status: AppointmentStatus) => {
        switch (status) {
          case AppointmentStatus.Confirmed:
            return <Tag color="processing" icon={<CheckCircleOutlined />}>{getStatusLabel(status)}</Tag>;
          case AppointmentStatus.Pending:
            return <Tag color="warning" icon={<ClockCircleOutlined />}>{getStatusLabel(status)}</Tag>;
          case AppointmentStatus.Rejected:
            return <Tag color="default" icon={<CloseCircleOutlined />}>{getStatusLabel(status)}</Tag>;
          case AppointmentStatus.Cancelled:
            return <Tag color="default" icon={<MinusCircleOutlined />}>{getStatusLabel(status)}</Tag>;
          default:
            return <Tag>--</Tag>;
        }
      },
    },
    {
      title: '操作',
      key: 'action',
      render: (_, record) => (
        <Space size="middle">
          {record.status === AppointmentStatus.Pending && (
            <>
              <Popconfirm
                title="确认预约"
                description="确定要接受这个预约吗？"
                onConfirm={() => confirmAppointment(record.id)}
                okText="确定"
                cancelText="取消"
              >
                <Button type="primary" size="small">
                  确认
                </Button>
              </Popconfirm>
              <Popconfirm
                title="拒绝预约"
                description="确定要拒绝这个预约吗？"
                onConfirm={() => rejectAppointment(record.id)}
                okText="确定"
                cancelText="取消"
              >
                <Button size="small">
                  拒绝
                </Button>
              </Popconfirm>
            </>
          )}
        </Space>
      ),
    },
  ];

  return (
    <>
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col flex="1 1 1px">
          <Space size="large">
            <Segmented
              options={[{ label: '未来预约', value: 'future' }, { label: '历史预约', value: 'history' }]} 
              value={appointmentType}
              onChange={handleTypeChange}
            />
            <Checkbox.Group
              options={statusOptions}
              value={selectedStatuses}
              onChange={handleStatusChange}
            />
          </Space>
        </Col>
        {/* 批量操作按钮 */}
        {selectedRowKeys.length > 0 && (
          <Col flex="0 0 auto">
            <Space size="large">
              <Popconfirm
                title="批量确认预约"
                description={`确定要确认选中的 ${selectedRowKeys.length} 个预约吗？`}
                onConfirm={confirmAppointments}
                okText="确定"
                cancelText="取消"
              >
                <Button type="primary">
                  批量接受
                </Button>
              </Popconfirm>
              <Popconfirm
                title="批量拒绝预约"
                description={`确定要拒绝选中的 ${selectedRowKeys.length} 个预约吗？`}
                onConfirm={rejectAppointments}
                okText="确定"
                cancelText="取消"
              >
                <Button>
                  批量拒绝
                </Button>
              </Popconfirm>
            </Space>
          </Col>
        )}
      </Row>
      <Table 
        columns={columns} 
        dataSource={appointments} 
        loading={loading}
        rowKey="id"
        pagination={false}
        rowSelection={rowSelection}  // 添加行选择功能
      />
      <Flex align='center' justify='center' style={{ marginTop: 16 }}>
        <Space.Compact>
          <Button icon={<LeftOutlined />} onClick={goToPreviousWeek}>
            上一周
          </Button>
          <Input readOnly style={{ width: 200, textAlign: 'center' }} value={formatWeekRange()} />
          <Button 
            icon={<RightOutlined />} 
            onClick={goToNextWeek}
            disabled={appointmentType === 'history' && currentWeekStart.isSameOrAfter(dayjs().startOf('week'), 'week')}
          >
            下一周
          </Button>
        </Space.Compact>
      </Flex>
    </>
  );
};

export default Appointments;